import React, { memo, useCallback } from 'react';
// import Header from '@/components/layout/header';
// import CelloLayout from './Cello';
import { hasIntegrationAtom } from '@/atoms/integrationAtoms';
import { hasCompletedOnboardingAtom } from '@/atoms/userCreationAtoms';
import ForceUpdate from '@/components/force-update/force-update';
import { IntegrationHeader } from '@/components/layout/header/integration-header';
import MobileAppHeader from '@/components/layout/header/mobile-app-header/mobile-app-header';
import {
  DeleteSessionModal,
  DeleteTranscriptModal,
} from '@/components/layout/lazy-session-dialogs';
import { routePath } from '@/components/layout/Navigation/sidebar/nav-item.utils';
import { SessionWorkPanelEvidenceChromeHost } from '@/composition/hosts/session-work-panel-evidence-chrome-host';
import { WorkCreditAllowanceChromeHost } from '@/composition/hosts/work-credit-allowance-chrome-host';
// Side-effect import — binds the app inputs the Work-home surfaces behind
// @heidi/agents read. It is here rather than in the Work route: the route is a
// Chat file, and an `@/utils` import there raises the Chat ledger's zone count.
// This layout is the route's own static import, evaluated before it renders.
import '@/utils/work-home-ports-wiring';

import { getConsentLegalLinks } from '@/components/utils/legalDocuments';
import { useIsDictationEnabled } from '@/composition/hosts/dictation/hooks/useIsDictationEnabled';
import TutorialChecklist from '@/container/tutorial/tutorial-panel';
import { DictationBubble } from '@/features/dictation/dictationBubble/ui/DictationBubble';
import { HEIDI_II_MASTER_FLAG_KEY } from '@/features/flags/heidi-work-umbrella';
import { IS_ONBOARDING_V2_TREATMENT } from '@/features/flags/legacy-flags';
import { useFeatureFlagEnabled } from '@/features/flags/posthog-compat';
import { clearCachedOnboardingPendingMark } from '@/features/onboarding-v2/hooks/onboardingCompletionOperations';
import { PerfDebugPanel } from '@/features/performanceTracking/perf-debug-panel';
import { useManualExposure } from '@/hooks/amplitude';
import { useDesktopConnectivity } from '@/hooks/desktop/useDesktopConnectivity';
import { EditorVersionPrefetch } from '@/hooks/editor-version/EditorVersionPrefetch';
import { useAuthToken } from '@/hooks/useAuthToken';
import { useRequireRegionGuard } from '@/hooks/useRequireRegionGuard';
import { useVisualViewportHeight } from '@/hooks/useVisualViewportHeight';
import { useIsMobile } from '@/hooks/useWindowDimensions';
import { inAppIntegrationLoadingAtom } from '@/integrations/in-app-integration-atom';
import { heidi2OnboardingTrackers } from '@/models/analytics/features/heidi-2-onboarding-events';
import { usePosthogFlagsReady } from '@/providers/posthog-flags';
import { isTauri } from '@/tauri/utils';
import { appendAttributionParamsToRedirectUrl } from '@/utils/attributionParams';
import { logger } from '@/utils/logger/logger';
import { useHeidi2BookDemoPort } from '@/utils/use-heidi-2-book-demo-port';
import { Heidi2AwarenessBanner, type Heidi2AwarenessBannerPorts } from '@heidi/agents';
import { useResumePendingOnboarding } from '@heidi/growth';
import { PerfTrackingStopper, TrackingKey } from '@heidi/runtime/performance';
import { type InAppHref, useCurrentSessionId } from '@heidi/runtime/shell';
import { useCacheUserRefIdForStorage } from '@heidi/session';
import { NEW_SESSION_LIST_PATH_PREFIX } from '@heidi/session/contracts';
import { useQueryUser, useUpdateUser } from '@heidi/workspace/data';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/router';
import { ResizableLayout } from './Navigation/ResizableLayout';
import ShortcutsHelper from './ShortcutsHelper/shortcuts-helper';

interface ScribeLayoutProps {
  children: React.ReactNode;
  topBanner?: React.ReactNode;
  /** Lazy route fallbacks must not finish the workspace arrival measurement. */
  trackNavigationArrival?: boolean;
  shouldShowDesktopSidebar?: boolean;
  shouldShowMobileSidebar?: boolean;
}

const Tutorial = memo(TutorialChecklist);

/**
 * The Heidi II inputs typed in the package's own `/ui` vocabulary — the tour's
 * trackers, its consent links and its book-a-demo port — plus the session path
 * a declined tour lands on. They cross here, at the banner's only mount, rather
 * than on the headless seam, which may not name a type from that zone.
 */
const heidi2AwarenessBannerPorts: Heidi2AwarenessBannerPorts = {
  newSessionListPathPrefix: NEW_SESSION_LIST_PATH_PREFIX,
  heidi2OnboardingTrackers,
  getConsentLegalLinks,
  useHeidi2BookDemoPort,
};

export const ScribeLayout = ({
  children,
  topBanner,
  trackNavigationArrival = true,
  shouldShowMobileSidebar,
  shouldShowDesktopSidebar = true,
}: ScribeLayoutProps) => {
  const isMobile = useIsMobile();
  useVisualViewportHeight({ enabled: isMobile });
  // Reroute authenticated-but-region-less users to onboarding so they can't
  // get stranded on an in-app page that never creates their user record.
  useRequireRegionGuard();
  const router = useRouter();
  const { pathname, asPath } = router;
  const authToken = useAuthToken();
  const { data: user, isSuccess: isUserLoaded } = useQueryUser();
  const hasCompletedOnboarding = useAtomValue(hasCompletedOnboardingAtom);
  const desktopConnectivity = useDesktopConnectivity();
  const areFlagsReady = usePosthogFlagsReady();
  const replaceRoute = useCallback(
    async (href: InAppHref) => {
      // `false` is a superseded navigation; report it like a failed one.
      if (!(await router.replace(href))) throw new Error('navigation cancelled');
    },
    [router]
  );
  const { mutateAsync: mutateUpdateUser } = useUpdateUser();
  const handleCannotResumeOnboarding = useCallback(() => {
    void (async () => {
      try {
        await mutateUpdateUser({ onboarding_completed: true });
        clearCachedOnboardingPendingMark();
      } catch {
        logger.warn('Failed to clear pending onboarding mark for a flag-off user');
      }
    })();
  }, [mutateUpdateUser]);
  useResumePendingOnboarding({
    canResume: IS_ONBOARDING_V2_TREATMENT(),
    isResumeDecided: areFlagsReady,
    hasAuthToken: !!authToken,
    isUserLoaded,
    onboardingPendingAt: user?.onboarding_pending_at,
    hasCompletedOnboarding,
    pathname,
    isFromSharedTemplate: router.query.from === 'shared-template',
    isDesktopOffline: desktopConnectivity === 'offline',
    preserveRedirectParams: appendAttributionParamsToRedirectUrl,
    onNavigate: replaceRoute,
    onCannotResume: handleCannotResumeOnboarding,
  });
  useCacheUserRefIdForStorage();
  const sessionId = useCurrentSessionId();

  const isHeidiIIMasterEnabled = useFeatureFlagEnabled(HEIDI_II_MASTER_FLAG_KEY);
  useManualExposure({
    flagKey: HEIDI_II_MASTER_FLAG_KEY,
    isEnabled: isHeidiIIMasterEnabled,
    isVisible: true,
  });

  const hasIntegration = useAtomValue(hasIntegrationAtom);
  const isInAppIntegrationLoading = useAtomValue(inAppIntegrationLoadingAtom);
  const isSessionless = pathname.includes('scribe/chat');
  const isEvidenceDictationEnabled = isSessionless;
  const isDictationEnabled = useIsDictationEnabled();
  // Show mobile header when:
  // 1. Not on a session route (no sessionId) OR explicitly requested via shouldShowMobileSidebar
  // 2. Not loading integration
  // 3. On mobile
  const shouldShowMobileHeader =
    !isInAppIntegrationLoading && (shouldShowMobileSidebar || (!sessionId && isMobile));

  return (
    <WorkCreditAllowanceChromeHost>
      <SessionWorkPanelEvidenceChromeHost>
        <div
          // h-[var(--vvh,100dvh)] and max-h-[...] have vh fallbacks in globals.css for Edge ≤107.
          // The top padding reserves the 32px the fixed `<TauriAppHeader />` (rendered
          // once globally in `_app.tsx`) overlays the top with — without this the
          // ResizableLayout / sidebar contents sit under the title bar. The var is
          // published by `TauriTitlebarHeightVar` (0px on web / aux windows).
          className="flex h-[var(--vvh,100dvh)] max-h-[var(--vvh,100dvh)] w-dvw flex-col pt-[var(--app-titlebar-height,0px)]"
        >
          {isTauri() && <ForceUpdate />}
          {hasIntegration && <IntegrationHeader />}
          {shouldShowMobileHeader && <MobileAppHeader />}
          <ShortcutsHelper />
          <EditorVersionPrefetch />
          <Heidi2AwarenessBanner ports={heidi2AwarenessBannerPorts} />
          {topBanner}
          <ResizableLayout
            shouldShowDesktopSidebar={shouldShowDesktopSidebar && !isMobile}
          >
            {children}
            {/* Closes the `perf.app.nav_menu_switch` flow `NavItem` opened on a
            sidebar click (ARD-1955): "arrived" = this layout committed the
            destination's children, one frame later. Every sidebar destination
            renders ScribeLayout, so one stopper covers the whole menu. Keyed on
            the query-stripped path so a same-page-file navigation (session A →
            session B) remounts it even though this layout instance survives.
            No `condition`: `finishTracking` no-ops when nothing is in flight. */}
            {trackNavigationArrival && (
              <PerfTrackingStopper
                key={routePath(asPath)}
                trackingKey={TrackingKey.APP_NAV_MENU_SWITCH}
              />
            )}
          </ResizableLayout>
          {!isMobile && <Tutorial />}
          {(isDictationEnabled || isEvidenceDictationEnabled) && <DictationBubble />}
          <DeleteSessionModal />
          <DeleteTranscriptModal />
          {process.env.NEXT_PUBLIC_PERF_DEBUG === 'true' && <PerfDebugPanel />}
        </div>
      </SessionWorkPanelEvidenceChromeHost>
    </WorkCreditAllowanceChromeHost>
  );
};
